import React from 'react';
import { useSocketContext, SocketStatus } from '$/components/Socket/SocketContext';
import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { HasJoined, PlayerMode } from '$/components/WebinarPlayer/types';
import { usePlaybackEventMutation } from '$/GraphQL/schema/schema';
import { PlaybackEventType, RoomType } from '$/GraphQL/schema/types';
import { logger } from '$/utils/logger';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { useServerContext } from '$/hooks/useServerDateNow';
import { useEmbeddedPlayerContext } from '$/components/EmbeddedPlayer/useEmbeddedPlayer';
import useRunOnce from '$/hooks/useRunOnce';

export const PlaybackEventNotifier: React.FC<{
  attendeeId: string;
  mode: PlayerMode;
}> = withWebinarPlayerContext(
  [
    'playbackPositionRef',
    'hasJoined',
    'lastSegmentRef',
    'setLastSegment',
    'player',
    'attendee',
    'playbackSpeed',
    'isPlaying',
    'currentRoom',
    'seekingFromRef',
    'loaded',
    'isSeeking',
    'pauseTimer',
    'isScrobbling',
    'scrobblingFromRef',
    'shortIdRef',
    'connectionId',
    'showingInVideoFormBeforeWatching',
  ],
  'PlaybackEventNotifier'
)(({ attendeeId, mode, context }) => {
  const [playbackEvent] = usePlaybackEventMutation();
  const {
    playbackPositionRef,
    hasJoined,
    lastSegmentRef,
    setLastSegment,
    player,
    attendee,
    playbackSpeed,
    isPlaying,
    currentRoom,
    seekingFromRef,
    loaded,
    isSeeking,
    pauseTimer,
    isScrobbling,
    scrobblingFromRef,
    shortIdRef,
    connectionId,
    showingInVideoFormBeforeWatching,
  } = context;
  const { status } = useSocketContext();
  const { serverDateNow } = useServerContext();
  const { isEmbeddedPlayer } = useEmbeddedPlayerContext();
  const [playbackEventMutation] = usePlaybackEventMutation();

  const connectionOrShortId = React.useMemo(() => {
    return connectionId ? connectionId : shortIdRef.current ?? '';
  }, [connectionId, shortIdRef]);

  const socketConnected = status === SocketStatus.Connected;

  const stillPlaying = React.useCallback(async (): Promise<void> => {
    try {
      logger.debug('PLAY', '[ PE ] stillPlaying ', playbackPositionRef.current);
      await playbackEvent({
        variables: {
          id: attendeeId,
          connectionId: connectionOrShortId,
          forReplay: mode === PlayerMode.replay,
          events: [
            {
              type: PlaybackEventType.StillPlaying,
              atDate: serverDateNow().toISOString(),
              atSecs: playbackPositionRef.current,
            },
          ],
        },
      });
    } catch (error) {
      logger.error('still playing heartbeat call failed', {
        error,
        attendeeId,
      });
    }
  }, [attendeeId, connectionOrShortId, mode, playbackEvent, playbackPositionRef, serverDateNow]);

  const socketStateHasChanged = useHasChangedShallow([socketConnected, connectionId]);
  React.useEffect(() => {
    if (hasJoined !== HasJoined.Joined || currentRoom.type === RoomType.PostExit || !isPlaying) {
      return;
    }

    // if the socket disconnected and reconnected, fire StillPlaying event to create a segment with new cid
    if (socketStateHasChanged && socketConnected && connectionId) {
      logger.debug('CHAT', 'Socket reconnected -> firing StillPlaying event');
      void stillPlaying();
      return;
    }

    // This only matters if the socket isn't connected
    if (socketConnected) return;
    logger.debug('CHAT', 'PLAYBACK EVENT 44: SETTING UP SOCKET STILLPLAYING INTERVAL');

    // Socket failed in presentation room but we're still playing so
    // tell backend in order to continue to track accurate duration

    const stillPlayingLive = (): void => {
      if (!isPlaying || socketConnected) return;
      void stillPlaying();
    };
    // Update server every 30 seconds
    const interval = setInterval(stillPlayingLive, 30 * 1000);
    return (): void => {
      clearInterval(interval);
    };
  }, [
    isPlaying,
    hasJoined,
    socketConnected,
    currentRoom.type,
    stillPlaying,
    socketStateHasChanged,
    connectionId,
  ]);

  const hasIsPlayingChanges = useHasChangedShallow([isPlaying]);
  const hasJoinedChanges = useHasChangedShallow([hasJoined]);
  // `hasJoinedChangesAndNotPlaying`: this occurs when the `Register in Video` form appears at '00:00:00' of an Ungated On-Demand session
  const hasJoinedChangesAndNotPlaying =
    hasJoinedChanges && !isPlaying && showingInVideoFormBeforeWatching;
  React.useEffect(() => {
    if (!hasIsPlayingChanges && !hasJoinedChangesAndNotPlaying) return;

    if (
      !loaded ||
      hasJoined !== HasJoined.Joined ||
      !attendee ||
      ![PlayerMode.live, PlayerMode.replay].includes(mode)
    ) {
      return;
    }

    // Make async so we can await the Mutation
    (async (): Promise<void> => {
      if (isPlaying) {
        logger.debug('PLAY', '[ PE ] onPlay ', playbackPositionRef.current, ' @ ', playbackSpeed);

        const event = {
          type: PlaybackEventType.Play,
          atDate: serverDateNow().toISOString(),
          atSecs: playbackPositionRef.current!,
          speed: playbackSpeed,
        };

        setLastSegment({
          startAtSecs: event.atSecs,
          startTime: event.atDate,
          speed: event.speed,
          lastEvent: event.type,
        });

        await playbackEventMutation({
          variables: {
            id: attendeeId,
            connectionId: connectionOrShortId,
            forReplay: mode === PlayerMode.replay,
            events: [event],
          },
        });
      } else {
        logger.debug('PLAY', '[ PE ] onPaused ', playbackPositionRef.current);

        const event = {
          type: PlaybackEventType.Pause,
          atDate: serverDateNow().toISOString(),
          atSecs: playbackPositionRef.current,
          extendsForSecs: pauseTimer,
        };

        const lastSegment = lastSegmentRef.current!;
        setLastSegment({
          startTime: lastSegment.startTime,
          startAtSecs: lastSegment.startAtSecs,
          speed: lastSegment.speed,
          endTime: event.atDate,
          endAtSecs: event.atSecs,
          extendedBySecs: event.extendsForSecs,
          lastEvent: event.type,
        });

        await playbackEventMutation({
          variables: {
            id: attendeeId,
            connectionId: connectionOrShortId,
            forReplay: mode === PlayerMode.replay,
            events: [event],
          },
        });
      }
    })();
  }, [
    attendeeId,
    attendee,
    connectionOrShortId,
    hasIsPlayingChanges,
    hasJoined,
    isPlaying,
    lastSegmentRef,
    loaded,
    mode,
    pauseTimer,
    playbackEventMutation,
    playbackPositionRef,
    playbackSpeed,
    player,
    serverDateNow,
    setLastSegment,
    hasJoinedChangesAndNotPlaying,
  ]);

  useRunOnce(() => {
    if (!isEmbeddedPlayer || mode !== PlayerMode.replay || !attendee) return;
    (async function () {
      logger.debug('PLAY', '[ PE ] onPlay ', playbackPositionRef.current, ' @ ', playbackSpeed);
      const event = {
        type: PlaybackEventType.Play,
        atDate: serverDateNow().toISOString(),
        atSecs: playbackPositionRef.current!,
        speed: playbackSpeed,
      };

      setLastSegment({
        startAtSecs: event.atSecs,
        startTime: event.atDate,
        speed: event.speed,
        lastEvent: event.type,
      });

      await playbackEventMutation({
        variables: {
          id: attendeeId,
          connectionId: connectionOrShortId,
          forReplay: mode === PlayerMode.replay,
          events: [event],
        },
      });
    })();
  });

  const hasIsSeekingChanges = useHasChangedShallow([isSeeking]);
  const hasScrobblingChanged = useHasChangedShallow([isScrobbling]);
  React.useEffect(() => {
    if (!hasIsSeekingChanges && !hasScrobblingChanged && !isScrobbling) return;

    if (
      isScrobbling ||
      !loaded ||
      hasJoined !== HasJoined.Joined ||
      isSeeking ||
      seekingFromRef.current === playbackPositionRef.current ||
      !isPlaying ||
      !attendee ||
      ![PlayerMode.live, PlayerMode.replay].includes(mode)
    ) {
      return;
    }

    // Make async so we can await the Mutation
    (async (): Promise<void> => {
      const seekingTo = playbackPositionRef.current!;
      let seekingFrom = seekingFromRef.current!;
      if (scrobblingFromRef.current !== null) {
        logger.debug('PLAY', '[ PE ] Using scrobblingFromRef');
        seekingFrom = scrobblingFromRef.current;
        scrobblingFromRef.current = null;
      }

      logger.debug('PLAY', '[ PE ] onSeeked ', seekingFrom, ' -> ', seekingTo);

      if (Math.abs(seekingTo - seekingFrom) < 5) {
        // Don't involve the server if the delta is too small.  Let's just assume constant playback
        logger.debug('PLAY', '[ PE ] Skipping - delta too small', {
          delta: Math.abs(seekingTo - seekingFrom),
        });
        return;
      }

      const playEvent = {
        type: PlaybackEventType.Play,
        atDate: serverDateNow().toISOString(),
        atSecs: seekingTo,
        speed: playbackSpeed,
      };

      setLastSegment({
        startAtSecs: playEvent.atSecs,
        startTime: playEvent.atDate,
        speed: playEvent.speed,
        lastEvent: playEvent.type,
      });

      // This is effectively a jump - which is a Pause of the current playback
      // and a play at the new position
      await playbackEventMutation({
        variables: {
          id: attendeeId,
          connectionId: connectionOrShortId,
          forReplay: mode === PlayerMode.replay,
          events: [
            {
              type: PlaybackEventType.Pause,
              atDate: serverDateNow().toISOString(),
              atSecs: seekingFrom,
              speed: playbackSpeed,
            },
            playEvent,
          ],
        },
      });
    })();
  }, [
    attendeeId,
    attendee,
    connectionOrShortId,
    hasIsSeekingChanges,
    hasJoined,
    hasScrobblingChanged,
    isPlaying,
    isScrobbling,
    isSeeking,
    lastSegmentRef,
    loaded,
    mode,
    playbackEventMutation,
    playbackPositionRef,
    playbackSpeed,
    player,
    scrobblingFromRef,
    seekingFromRef,
    serverDateNow,
    setLastSegment,
  ]);

  const hasSpeedChanged = useHasChangedShallow([playbackSpeed]);
  React.useEffect(() => {
    if (!hasSpeedChanged) return;

    if (!attendee || !isPlaying || !loaded || hasJoined !== HasJoined.Joined) return;

    // Make async so we can await the Mutation
    (async (): Promise<void> => {
      logger.debug('PLAY', 'PlaybackEvent: updateSpeed to ', playbackSpeed);

      const playEvent = {
        type: PlaybackEventType.Play,
        atDate: serverDateNow().toISOString(),
        atSecs: playbackPositionRef.current!,
        speed: playbackSpeed,
      };

      setLastSegment({
        startAtSecs: playEvent.atSecs,
        startTime: playEvent.atDate,
        speed: playEvent.speed,
        lastEvent: playEvent.type,
      });

      // Playback speed just changed
      await playbackEventMutation({
        variables: {
          id: attendeeId,
          connectionId: connectionOrShortId,
          forReplay: mode === PlayerMode.replay,
          events: [
            {
              type: PlaybackEventType.Pause,
              atDate: serverDateNow().toISOString(),
              atSecs: playbackPositionRef.current,
            },
            playEvent,
          ],
        },
      });
    })();
  }, [
    attendeeId,
    playbackPositionRef,
    playbackSpeed,
    hasSpeedChanged,
    attendee,
    isPlaying,
    playbackEventMutation,
    connectionOrShortId,
    loaded,
    hasJoined,
    mode,
    setLastSegment,
    serverDateNow,
  ]);

  return null;
});
